/**
 * Hook d'accès au verrouillage de conversation (couche React/Convex).
 *
 * Branche `convex/conversationLocks.ts`. L'org active = 1er account du user
 * (même convention que `usePermissions` / le reste du shell). Expose :
 *   - `locksByConv` : Map<conversationId, ConvLock> des verrous ACTIFS de l'org
 *     (réactif Convex → MAJ quasi-instantanée chez tous les opérateurs).
 *   - `viewFor(id)` : décision d'affichage/gating prête à l'emploi (cf viewLock).
 *   - `isBypass` / `myWorkosUserId` : identité + rôle du caller.
 *   - `acquire` / `reconcile` / `release` / `forceRelease` : mutations câblées
 *     sur l'account actif.
 *
 * Le CYCLE DE VIE (acquire à l'ouverture, heartbeat, libération à la fermeture)
 * est piloté par `ConversationLockController`. Ce hook ne fait qu'exposer l'état
 * + les actions.
 */
import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "convex/react";

import { api } from "@/../convex/_generated/api";
import type { Id } from "@/../convex/_generated/dataModel";
import { useActiveAccount } from "@/core/auth/activeAccount";
import { useSession } from "@/core/auth/useSession";
import { usePermissions } from "@/core/auth/usePermissions";

import {
  indexLocksByConversation,
  isBypassRole,
  viewLock,
  type ConvLock,
  type LockView,
} from "./lockLogic";

export interface UseConversationLocks {
  accountId: Id<"accounts"> | null;
  myWorkosUserId: string | null;
  /** True si l'utilisateur est superviseur (ceo/manager/superadmin) : il passe
   *  outre les verrous et n'en pose jamais. */
  isBypass: boolean;
  /** Verrous actifs de l'org, indexés par conversationId. */
  locksByConv: Map<string, ConvLock>;
  /** Décision d'affichage/gating pour une conversation (cf viewLock). Le caller
   *  passe son propre `now` (ex. le tick du panneau) pour la péremption locale ;
   *  défaut = Date.now() au moment de l'appel. */
  viewFor: (conversationId: string, now?: number) => LockView;
  /** Pose le verrou (gate anti-course). Throw ConvexError ALREADY_LOCKED si
   *  tenu par un autre. No-op (ok) pour les bypass / sans account. */
  acquire: (
    conversationId: string,
  ) => Promise<{ ok: boolean; bypass: boolean }>;
  /** Réconcilie les verrous du caller avec ses onglets ouverts (heartbeat +
   *  libération). Best-effort (ne throw pas). */
  reconcile: (openConversationIds: string[]) => Promise<void>;
  /** Libère SON propre verrou (fermeture d'onglet). */
  release: (conversationId: string) => Promise<void>;
  /** Force la libération (manager/ceo/superadmin) — casse un verrou coincé. */
  forceRelease: (conversationId: string) => Promise<void>;
  isLoading: boolean;
}

export function useConversationLocks(): UseConversationLocks {
  const { user } = useSession();
  const { roleSlug } = usePermissions();
  const myWorkosUserId = user?.workosUserId ?? null;
  const isBypass = isBypassRole(roleSlug, user?.isSuperadmin === true);

  // Org active = org de session (cf useActiveAccount) — pas accounts[0], qui
  // pointerait sur la mauvaise org pour un chatter multi-org.
  const { accountId, isLoading: accountLoading } = useActiveAccount();

  const locks = useQuery(
    api.conversationLocks.listForAccount,
    accountId ? { accountId } : "skip",
  ) as ConvLock[] | undefined;

  const locksByConv = useMemo(
    () => indexLocksByConversation(locks ?? []),
    [locks],
  );

  const acquireMut = useMutation(api.conversationLocks.acquire);
  const reconcileMut = useMutation(api.conversationLocks.reconcileLocks);
  const releaseMut = useMutation(api.conversationLocks.release);
  const forceReleaseMut = useMutation(api.conversationLocks.forceRelease);

  const acquire = useCallback(
    async (conversationId: string) => {
      if (!accountId) return { ok: true, bypass: true };
      return await acquireMut({ accountId, conversationId });
    },
    [accountId, acquireMut],
  );

  const reconcile = useCallback(
    async (openConversationIds: string[]) => {
      if (!accountId) return;
      try {
        await reconcileMut({ accountId, openConversationIds });
      } catch {
        // Heartbeat best-effort : un échec ponctuel est rattrapé au tick suivant.
      }
    },
    [accountId, reconcileMut],
  );

  const release = useCallback(
    async (conversationId: string) => {
      if (!accountId) return;
      try {
        await releaseMut({ accountId, conversationId });
      } catch {
        // Idempotent côté serveur ; en cas d'échec le TTL libère de toute façon.
      }
    },
    [accountId, releaseMut],
  );

  const forceRelease = useCallback(
    async (conversationId: string) => {
      if (!accountId) return;
      await forceReleaseMut({ accountId, conversationId });
    },
    [accountId, forceReleaseMut],
  );

  const viewFor = useCallback(
    (conversationId: string, now?: number): LockView =>
      viewLock(
        locksByConv.get(conversationId),
        now ?? Date.now(),
        myWorkosUserId,
        isBypass,
      ),
    [locksByConv, myWorkosUserId, isBypass],
  );

  const isLoading =
    accountLoading || (accountId !== null && locks === undefined);

  return {
    accountId,
    myWorkosUserId,
    isBypass,
    locksByConv,
    viewFor,
    acquire,
    reconcile,
    release,
    forceRelease,
    isLoading,
  };
}
